<script setup lang="ts">
import { ref } from 'vue';
import { MessageSquare, X } from 'lucide-vue-next';
import { useTranslations } from '../composables/useTranslations';
import { push } from 'firebase/database';
import { db } from '../configs/firebase';
import { ref as dbRef } from 'firebase/database';
import { getSentry } from '../lazy/sentry';

const props = defineProps<{
  type: 'fab' | 'button';
}>();

const t = useTranslations();

const isOpen = ref(false);
const name = ref('');
const email = ref('');
const message = ref('');
const isSending = ref(false);
const isSuccess = ref(false);

const handleSubmit = async (e: Event) => {
  e.preventDefault();
  isSending.value = true;

  try {
    await push(dbRef(db, '/feedback'), {
      name: name.value,
      email: email.value,
      message: message.value,
      timestamp: Date.now(),
    });

    const Sentry = await getSentry();

    Sentry.captureFeedback({
      name: name.value,
      email: email.value,
      message: message.value,
    });
    isSuccess.value = true;
  } catch (error) {
    console.error('Failed to send feedback:', error);
  } finally {
    isSending.value = false;
    isOpen.value = false;
    isSuccess.value = false;
    name.value = '';
    email.value = '';
    message.value = '';
  }
};
</script>

<template>
  <button
    :aria-label="t.giveFeedback"
    class="p-4 sm:px-6 bg-bg-secondary rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center gap-2 z-10"
    :class="props.type === 'fab' ? 'fixed bottom-6 right-6' : ''"
    @click="isOpen = true"
  >
    <MessageSquare class="w-6 h-6" />
    <span class="hidden sm:inline font-medium">{{ t.giveFeedback }}</span>
  </button>

  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <div
        class="bg-white rounded-xl shadow-xl w-full max-w-lg relative overflow-hidden"
      >
        <div class="p-6 sm:p-8">
          <div class="flex justify-between items-center">
            <h2 class="text-2xl sm:text-3xl font-semibold">
              {{ t.yourVoiceMatters }}
            </h2>
            <button
              class="p-2 text-secondary hover:text-secondary hover:bg-gray-100 rounded-lg transition-colors"
              @click="isOpen = false"
            >
              <X class="w-5 h-5" />
            </button>
          </div>
          <h4 class="text-sm text-gray-500 mb-6">Version: 0.1.1</h4>

          <p class="text-secondary mb-6">{{ t.shareFeedbackSubtitle }}</p>

          <form class="space-y-4" @submit="handleSubmit">
            <div>
              <label for="name" class="block text-sm font-medium mb-1">
                {{ t.enterName }}
              </label>
              <input
                id="name"
                v-model="name"
                type="text"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-shadow"
                :placeholder="t.enterName"
              />
            </div>

            <div>
              <label for="email" class="block text-sm font-medium mb-1">
                {{ t.email }}
              </label>
              <input
                id="email"
                v-model="email"
                type="email"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-shadow"
                placeholder="email@example.com"
              />
            </div>

            <div>
              <label for="message" class="block text-sm font-medium mb-1">
                {{ t.whyYourFeedBackIsCrucial }}
              </label>
              <textarea
                id="message"
                v-model="message"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-shadow min-h-[120px] resize-y"
                :placeholder="t.whyYourFeedBackIsCrucial"
                required
              />
            </div>

            <button
              type="submit"
              :disabled="isSending || isSuccess"
              class="w-full px-6 py-3 bg-accent text-white rounded-full font-medium hover:bg-accent transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
            >
              {{
                isSuccess
                  ? t.thankYouForYourFeedback
                  : isSending
                    ? t.loading
                    : t.sendFeedback
              }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>
